import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useGetProjectByIdQuery, V1ProjectLocationResource } from "@generated";
import { Project } from "../types/Project";

const useGetProject = (id) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  let customError: string | null = null; // Declare customError

  const { data, isLoading, error } = useGetProjectByIdQuery({
    slug: accountSlug,
    id,
    include: [
      "project-services",
      //@ts-ignore,
      "project-services.project-resource",
      "quotes",
      //@ts-ignore,
      "quotes.vendor",
      "project-products",
      "project-governances",
      // @ts-ignore
      "project-governances.project-resource",
      // @ts-ignore
      "project-governances.project-phase",
      "project-resources",
      // @ts-ignore
      "project-resources.line-of-business",
    ],
  });

  let project: Project | undefined = undefined;

  if (data?.data) {
    try {
      const p = data.data;
      if (p?.id && p?.relationships) {
        const services =
          p.relationships?.["project-services"]?.data?.map((ps) => {
            const service = data?.included?.find?.(
              (item) => item.type === "project-services" && item.id == ps.id
            ) as any;
            const resource = data?.included?.find?.(
              (item) =>
                item.type === "project-resources" &&
                item.id ==
                  service?.relationships?.["project-resource"]?.data?.id
            ) as any;
            return {
              id: String(service?.id || ""),
              type: service?.attributes?.["service-type"] || "",
              name: String(service?.attributes?.name || ""),
              selected: true,
              quantity: service?.attributes?.quantity,
              hours: service?.attributes?.["total-hours"],
              price:
                service?.attributes?.["calculated-pricing"]?.[
                  "service_revenue"
                ],
              cost: service?.attributes?.["calculated-pricing"]?.[
                "service_cost"
              ],
              resource:
                resource?.attributes?.["extended-name"] ||
                resource?.attributes?.name ||
                "",
            };
          }) || [];

        const quotes =
          p.relationships?.["quotes"]?.data?.map((q) => {
            const quote = data?.included?.find?.(
              (item) => item.type === "quotes" && item.id == q.id
            ) as any;
            const vendor = data?.included?.find?.(
              (item) =>
                item.type === "vendors" &&
                item.id == quote?.relationships?.["vendor"]?.data?.id
            ) as any;
            return {
              id: String(quote?.id || ""),
              name: String(vendor?.attributes?.name || ""),
              selected: true,
              cost: quote?.attributes?.cost,
              recCost: quote?.attributes?.["recurring-cost"],
              revenue: quote?.attributes?.price,
              recRevenue: quote?.attributes?.["recurring-price"],
            };
          }) || [];

        const products =
          p.relationships?.["project-products"]?.data?.map((p) => {
            const product = data?.included?.find?.(
              (item) => item.type === "project-products" && item.id == p.id
            ) as any;
            return {
              id: String(product?.id || ""),
              name: String(product?.attributes?.name || ""),
              selected: true,
              quantity: product?.attributes?.quantity,
              mfrPart: product?.attributes?.["manufacturer-part-number"],
              units: product?.attributes?.["unit-of-measure"],
              price: product?.attributes?.["unit-price"],
            };
          }) || [];

        const governances =
          p.relationships?.["project-governances"]?.data?.map((g) => {
            const governance = data?.included?.find?.(
              (item) => item.type === "project-governances" && item.id == g.id
            ) as any;
            const resource = data?.included?.find?.(
              (item) =>
                item.type === "project-resources" &&
                item.id ==
                  governance?.relationships?.["project-resource"]?.data?.id
            ) as any;
            const phase = data?.included?.find?.(
              (item) =>
                item.type === "project-phases" &&
                item.id ==
                  governance?.relationships?.["project-phase"]?.data?.id
            ) as any;
            return {
              id: String(governance?.id || ""),
              name: String(governance?.attributes?.description || ""),
              selected: true,
              resource: resource?.attributes?.name || "",
              phase: phase?.attributes?.name || "",
              calculation: governance?.attributes?.["calculation-type"] || "",
              hours: governance?.attributes?.hours,
            };
          }) || [];

        const resources =
          p.relationships?.["project-resources"]?.data?.map((r) => {
            const resource = data?.included?.find?.(
              (item) => item.type === "project-resources" && item.id == r.id
            ) as any;
            const lob = data?.included?.find?.(
              (item) =>
                item.type === "line-of-businesses" &&
                item.id ==
                  resource?.relationships?.["line-of-business"]?.data?.id
            ) as any;
            return {
              id: String(resource?.id || ""),
              name: String(resource?.attributes?.name || ""),
              lob: lob?.attributes?.["nested-name"] || "",
              hourlyRate: resource?.attributes?.["hourly-rate"],
              expenseRate: resource?.attributes?.["expense-rate"],
              selected: true,
            };
          }) || [];

        project = {
          id: p.id,
          services,
          quotes,
          products,
          governances,
          resources,
        };
      }
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return {
    project,
    isLoading,
    error: error ?? customError,
  };
};
export default useGetProject;
